import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { setMeta } from '../lib/seo';
import { ALBERTA_RESTAURANTS, ALBERTA_FOOD_TRUCKS } from '../data/restaurants';
import Footer from './Footer';

const ALL = [...ALBERTA_RESTAURANTS, ...ALBERTA_FOOD_TRUCKS];
const byId = (id) => ALL.find((r) => r.id === id);

// Renders **bold** markdown inline within a paragraph.
const renderParagraph = (text, i) => {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return (
    <p key={i} className="text-gray-800 leading-relaxed mb-5 text-base sm:text-lg">
      {parts.map((p, j) =>
        p.startsWith('**') && p.endsWith('**') ? <strong key={j}>{p.slice(2, -2)}</strong> : <span key={j}>{p}</span>
      )}
    </p>
  );
};

export default function LandingPage({
  testid,
  slug,
  title,
  metaTitle,
  metaDescription,
  heroImage,
  heroImageUrl,
  heroAlt,
  kicker,
  intro,
  picksHeading,
  restaurantIds,
  ctaHeading,
  ctaText,
  faqs,
  wizardCity,
  wizardCuisines,
}) {
  const heroSrc = heroImageUrl || `/images/${heroImage}`;
  useEffect(() => {
    setMeta({
      title: metaTitle,
      description: metaDescription,
      canonical: `https://culinera.ca/${slug}`,
      image: heroImageUrl || `https://culinera.ca/images/${heroImage}`,
      jsonLd: {
        '@context': 'https://schema.org',
        '@graph': [
          {
            '@type': 'WebPage',
            name: metaTitle,
            description: metaDescription,
            url: `https://culinera.ca/${slug}`,
          },
          {
            '@type': 'FAQPage',
            mainEntity: (faqs || []).map((f) => ({
              '@type': 'Question',
              name: f.q,
              acceptedAnswer: { '@type': 'Answer', text: f.a },
            })),
          },
        ],
      },
    });
  }, [metaTitle, metaDescription, slug, heroImage, heroImageUrl, faqs]);

  const picks = (restaurantIds || []).map(byId).filter(Boolean);

  const wizardLink = (() => {
    const qs = new URLSearchParams({ view: 'wizard' });
    if (wizardCity) qs.set('city', wizardCity);
    if (wizardCuisines && wizardCuisines.length) qs.set('cuisines', wizardCuisines.join(','));
    return `/?${qs.toString()}`;
  })();

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-cyan-50" data-testid={testid}>
      <header className="bg-white shadow-sm">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 flex justify-between items-center py-4">
          <Link to="/" className="flex items-center space-x-3" data-testid="landing-logo-link">
            <div className="w-8 h-8 bg-gradient-to-br from-teal-600 to-cyan-600 rounded-full flex items-center justify-center">
              <span className="text-white font-bold text-sm">C</span>
            </div>
            <span className="text-xl font-bold text-gray-900">Culinera</span>
          </Link>
          <nav className="flex items-center gap-5 text-sm font-semibold">
            <Link to="/blog" className="text-teal-600 hover:text-teal-700">Journal</Link>
            <Link to="/?view=restaurants" className="text-teal-600 hover:text-teal-700">Restaurants</Link>
            <Link to="/" className="text-teal-600 hover:text-teal-700" data-testid="landing-home-link">Home</Link>
          </nav>
        </div>
      </header>

      {/* Hero */}
      <section className="relative">
        <div className="relative h-[340px] sm:h-[440px] w-full overflow-hidden">
          <img
            src={heroSrc}
            alt={heroAlt}
            className="absolute inset-0 w-full h-full object-cover"
            data-testid="landing-hero-image"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-gray-900/85 via-gray-900/40 to-gray-900/20" />
          <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 h-full flex flex-col justify-end pb-10">
            <span className="text-cyan-300 font-semibold uppercase tracking-wide text-xs sm:text-sm mb-3">{kicker}</span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight max-w-3xl">{title}</h1>
          </div>
        </div>
      </section>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <nav className="text-sm text-gray-500 mb-6" aria-label="Breadcrumb">
          <Link to="/" className="hover:text-teal-600">Home</Link> › <span className="text-gray-900">{kicker}</span>
        </nav>

        <article className="prose-styled">{intro.map(renderParagraph)}</article>

        <div className="my-8 bg-white rounded-xl shadow-sm border border-teal-100 p-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <div className="font-bold text-gray-900 text-lg">Turn this list into a day-by-day trip</div>
            <p className="text-sm text-gray-600">Build a free personalized food itinerary in under a minute.</p>
          </div>
          <Link
            to={wizardLink}
            data-testid="landing-wizard-cta-top"
            className="whitespace-nowrap bg-gradient-to-r from-teal-600 to-cyan-600 text-white px-5 py-3 rounded-lg font-semibold hover:from-teal-700 hover:to-cyan-700 transition-colors"
          >
            Plan this trip →
          </Link>
        </div>

        {picks.length > 0 && (
          <section className="mt-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-5">{picksHeading}</h2>
            <ul className="grid sm:grid-cols-2 gap-4">
              {picks.map((r) => (
                <li key={r.id}>
                  <Link
                    to={`/restaurants/${r.id}`}
                    data-testid={`landing-restaurant-${r.id}`}
                    className="block bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow p-5 h-full"
                  >
                    <div className="flex items-center justify-between gap-2 mb-1">
                      <span className="font-bold text-gray-900">{r.name}</span>
                      <span className="text-xs text-gray-500">{r.price}</span>
                    </div>
                    <div className="text-xs text-teal-700 font-semibold mb-2">{[r.cuisine, [r.neighborhood, r.city].filter(Boolean).join(', ')].filter(Boolean).join(' · ')}</div>
                    <p className="text-sm text-gray-700 leading-snug">{r.description}</p>
                  </Link>
                </li>
              ))}
            </ul>
          </section>
        )}

        <section className="mt-12 bg-gradient-to-br from-teal-600 to-cyan-600 rounded-2xl p-8 text-center text-white">
          <h2 className="text-2xl font-bold mb-2">{ctaHeading}</h2>
          <p className="text-cyan-50 mb-6 max-w-xl mx-auto">{ctaText}</p>
          <Link
            to={wizardLink}
            data-testid="landing-wizard-cta-bottom"
            className="inline-block bg-white text-teal-700 px-6 py-3 rounded-lg font-bold hover:bg-cyan-50 transition-colors"
          >
            Plan this trip free →
          </Link>
        </section>

        {faqs && faqs.length > 0 && (
          <section className="mt-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-5">Frequently asked</h2>
            <div className="space-y-4">
              {faqs.map((f, i) => (
                <div key={i} className="bg-white rounded-lg shadow-sm p-5">
                  <h3 className="font-semibold text-gray-900 mb-1">{f.q}</h3>
                  <p className="text-sm text-gray-700 leading-relaxed">{f.a}</p>
                </div>
              ))}
            </div>
          </section>
        )}

        <div className="mt-12 pt-8 border-t border-gray-200 text-sm text-gray-500">
          Looking for more Alberta food inspiration? Read the{' '}
          <Link to="/blog" className="text-teal-600 hover:underline font-semibold">Culinera Food Journal</Link> or{' '}
          <Link to="/" className="text-teal-600 hover:underline font-semibold">build your itinerary →</Link>
        </div>
      </main>
      <Footer />
    </div>
  );
}
